<template>
    <div
        ref="questionRow"
        :class="[
            'question-row',
            'question-row-buttons-with-image',
            {
                'question-row-buttons-with-image--rounded-corners': props.content.options.rounded_corners.value,
            }
        ]"
        :data-has-two-sides="props.content.options.has_two_sides.value"
        :data-image-position-on-desktop="props.content.options.image_position_on_desktop.value"
        :data-content-position-on-desktop="props.content.options.content_position_on_desktop.value"
        :data-animation="props.content.options.animation.value"
        :data-lead-service-name="props.content.lead_service_name.value || `extradata-question-${index}`"
    >
        <div
            v-for="(answer, index) in props.content.answers"
            :key="index"
            class="button-with-image"
            :class="[
                'button-with-image',
                {
                    'button-with-image--active': selectedQuestions.includes(index),
                    'button-with-image--rounded': props.content.options.rounded_corners.value,
                }
            ]"
            :data-index="index"
            :data-value="answer.ELMO_value.value"
            :data-answer="answer.ELMO_value.value"
            :data-showstopper="answer.showstopper.value"
            :data-question-to="answer.next_question.value"
            @click="itemClick(index)"
        >
            <div class="button-with-image__front-side">
                <zoe-atom-media
                    class="button-with-image__image"
                    :item="answer.image.value"
                    @load="handleImageLoading(index)"
                />

                <zoe-atom-text
                    class="button-with-image__text"
                    :item="answer.text.value"
                />
            </div>

            <div
                v-if="props.content.options.has_two_sides.value"
                class="button-with-image__back-side"
            >
                <zoe-atom-text
                    class="button-with-image__back-side-text"
                    :item="answer.backside_text.value"
                />
            </div>
        </div>
    </div>

    <div
        v-if="props.content.options.has_two_sides.value || props.content.options.select_multiple_buttons.value"
        class="question-row question-row-button"
    >
        <button
            class="question-button-next button"
            type="button"
            value="next"
            data-showstopper="none"
            @click="check"
        >
            <zoe-atom-text
                :item="props.content.button_text.value"
            />
        </button>
    </div>
</template>

<script setup>
import { nextTick, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { addExtraData, getQuestionName, questionProps, storeExtraData } from '@mixins/quiz/v2.0.0/question';

import ZoeAtomMedia from '../../../../atoms/media/v1.0.0/zoe-atom-media.vue';
import ZoeAtomText from '../../../../atoms/text/v1.0.0/zoe-atom-text.vue';
import { setInvalid } from '@mixins/quiz/v2.0.0/invalid';
import { getNextQuestion } from '@mixins/quiz/v2.0.0/get-next-question';
import { checkCompleted } from '@mixins/quiz/v2.0.0/checks';
import stripHtml from '../../../../../mixins/utils/strip-html';

const store = useStore();
const props = defineProps(questionProps);
const emit = defineEmits(['imageHasLoaded']);

const name = getQuestionName(props);
const uuid = ref(props.content?._info?.uuid);

const questionRow = ref(null);
const selectedQuestions = ref([]);

onMounted(() => {
    addExtraData(name, {
        name,
        value: '',
        mask: '',
        validator: '',
        uuid: uuid.value,
        sendData: props.content.send_to_lead_service?.value !== false,
    });

    storeExtraData(name, 'question', stripHtml(props.content.text.value.text || props.content.text.value));
});

onMounted(() => {
    nextTick(() => {
        document.body.focus();
    });
});

const itemClick = index => {
    if (selectedQuestions.value.includes(index)) {
        selectedQuestions.value.splice(selectedQuestions.value.indexOf(index), 1);
    } else {
        if (!props.content.options.select_multiple_buttons.value) {
            selectedQuestions.value = [];
        }

        selectedQuestions.value.push(index);

        if (!props.content.options.has_two_sides.value && !props.content.options.select_multiple_buttons.value) {
            check();
        }
    }
};

const handleImageLoading = index => {
    emit('imageHasLoaded', { index: index });

    if (!props.content.options.has_two_sides.value) return;

    const button = questionRow.value.querySelector(`[data-index="${index}"]`);
    const buttonFrontside = button.querySelector('.button-with-image__front-side');
    const buttonBackside = button.querySelector('.button-with-image__back-side');

    if (buttonBackside.offsetHeight > buttonFrontside.offsetHeight) {
        buttonFrontside.style.position = 'absolute';
        buttonBackside.style.position = 'relative';
    }
};

const check = event => {
    const buttons = questionRow.value.querySelectorAll('[data-answer]');

    if (document.activeElement.classList.contains('button')) document.activeElement.blur();

    if (selectedQuestions.value.length === 0) {
        setInvalid(questionRow.value);
        return;
    }

    let showstopper = false;

    selectedQuestions.value.forEach(question => {
        const questionObject = props.content.answers[question];

        if (questionObject.showstopper.value !== '' && questionObject.showstopper.value !== 'none') {
            if (questionObject.showstopper.hide_showstopper_close_button) store.commit('dialog/preventClose');
            store.commit('dialog/show', questionObject.showstopper.value);
            showstopper = true;
        }
    });

    if (showstopper) return;

    let nextQuestionValue = '';

    if (store.state.quiz.advancedFlow) {
        if (props.content.options.select_multiple_buttons.value) {
            nextQuestionValue = props.content.next_question.value;
        } else {
            nextQuestionValue = props.content.answers[selectedQuestions.value[0]].next_question.value;
        }
    }

    const answerValue = selectedQuestions.value.map(question => props.content.answers[question].ELMO_value.value).join(', ');
    const answerLabel = selectedQuestions.value.map(question => stripHtml(props.content.answers[question].text.value)).join(', ');
    const linkedFormInputIdentifier = props.content.options.linked_form_input.value;
    const worktypeId = props.content.options.select_multiple_buttons ? '' : props.content.answers[selectedQuestions.value[0]].worktype_id_override.value;
    const enquiryDashboardRedirectVersion = props.content.options.select_multiple_buttons ? '' : props.content.answers[selectedQuestions.value[0]].enquiry_dashboard_redirect_version.value;
    const nextQuestion = getNextQuestion(nextQuestionValue, store.state.quiz.currentQuestion);

    checkCompleted(buttons, buttons, props.preHook, nextQuestion, props.afterHook, answerValue, linkedFormInputIdentifier, worktypeId, enquiryDashboardRedirectVersion, 0);
    storeExtraData(name, 'value', answerValue);
    storeExtraData(name, 'label', answerLabel);
};
</script>

<style lang="scss">
@use 'zoe-quiz-buttons-with-image';
</style>
